import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { resolveFileUrl } from '../utils/fileUrl';
import { toast } from 'sonner';
import {
  X, Save, Camera, User, Loader2, AlertCircle, MessageCircle, XCircle,
  Briefcase, MapPin, Phone, Globe, Linkedin, Home
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const FIELD_LABELS = {
  fullName: 'Full Name / पूरा नाम',
  fatherName: "Father's Name / पिता का नाम",
  motherName: "Mother's / Husband's Name / माता / पति का नाम",
  nukh: 'Nukh / नुख',
  dateOfBirth: 'Date of Birth / जन्म तिथि',
  email: 'Email / ईमेल',
  phone: 'Phone / फ़ोन',
  whatsapp: 'WhatsApp / व्हाट्सएप',
  country: 'Country / देश',
  state: 'State / राज्य',
  city: 'City / शहर',
  currentPlace: 'Area / Locality / क्षेत्र',
  pincode: 'Pincode / पिनकोड',
  address: 'Address / पता',
  nativeVillage: 'Native Village / मूल गाँव',
  nativeCity: 'Native City / मूल शहर',
  nativeState: 'Native State / मूल राज्य',
  nativeCountry: 'Native Country / मूल देश',
  profession: 'Profession / पेशा',
  professionOther: 'Profession (Other) / पेशा (अन्य)',
  specialization: 'Specialization / विशेषज्ञता',
  qualification: 'Qualification / योग्यता',
  experienceYears: 'Experience (Years) / अनुभव (वर्ष)',
  organization: 'Organization / संस्था',
  designation: 'Designation / पद',
  officeAddress: 'Office Address / कार्यालय पता',
  bio: 'Bio / परिचय',
  website: 'Website / वेबसाइट',
  linkedIn: 'LinkedIn / लिंक्डइन',
  additionalInfo: 'Additional Information / अतिरिक्त जानकारी'
};

const ProfessionalEditProfileModal = ({ open, profile, options, onClose, onSaved }) => {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [currentPhoto, setCurrentPhoto] = useState('');
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('professionalToken');

  useEffect(() => {
    if (profile) {
      setFormData({
        fullName: profile.fullName || '',
        fatherName: profile.fatherName || '',
        motherName: profile.motherName || '',
        nukh: profile.nukh || '',
        dateOfBirth: profile.dateOfBirth || '',
        email: profile.email || '',
        phone: profile.phone || '',
        whatsapp: profile.whatsapp || '',
        country: profile.country || 'India',
        state: profile.state || '',
        city: profile.city || '',
        currentPlace: profile.currentPlace || '',
        pincode: profile.pincode || '',
        address: profile.address || '',
        nativeVillage: profile.nativeVillage || '',
        nativeCity: profile.nativeCity || '',
        nativeState: profile.nativeState || '',
        nativeCountry: profile.nativeCountry || 'India',
        profession: profile.profession || '',
        professionOther: profile.professionOther || '',
        specialization: profile.specialization || '',
        qualification: profile.qualification || '',
        experienceYears: profile.experienceYears || '',
        organization: profile.organization || '',
        designation: profile.designation || '',
        officeAddress: profile.officeAddress || '',
        bio: profile.bio || '',
        website: profile.website || '',
        linkedIn: profile.linkedIn || '',
        interestedInConclave2026: profile.interestedInConclave2026 || false,
        additionalInfo: profile.additionalInfo || ''
      });
      setCurrentPhoto(profile.profilePhoto || '');
    }
  }, [profile]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG, PNG or WebP images are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be smaller than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const res = await fetch(`${BACKEND_URL}/api/professionals/me/photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (res.ok) {
        const data = await res.json();
        setCurrentPhoto(data.photoUrl);
        toast.success('Photo updated');
      } else {
        toast.error('Failed to upload photo');
      }
    } catch (err) {
      toast.error('Photo upload failed');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!formData.fullName?.trim()) {
      toast.error('Full name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...formData };
      // Cast experience to number if provided
      if (payload.experienceYears !== '' && payload.experienceYears !== null) {
        const n = parseInt(payload.experienceYears);
        payload.experienceYears = Number.isFinite(n) ? n : null;
      }

      const res = await fetch(`${BACKEND_URL}/api/professionals/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(
          data.status === 'pending'
            ? 'Profile updated! Changes will reflect after admin approval.'
            : 'Profile updated successfully!'
        );
        onSaved?.(data);
        onClose?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update profile');
      }
    } catch (err) {
      toast.error('An error occurred while saving');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4"
        onClick={onClose}
        data-testid="edit-profile-modal-backdrop"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden shadow-2xl flex flex-col"
          onClick={(e) => e.stopPropagation()}
          data-testid="edit-profile-modal"
        >
          {/* Header */}
          <div className="gradient-orange px-6 py-4 flex items-center justify-between">
            <div className="text-white">
              <h2 className="text-xl font-bold">Edit My Profile</h2>
              <p className="text-white/80 text-sm">मेरी प्रोफ़ाइल संपादित करें</p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white p-1 hover:bg-white/10 rounded"
              data-testid="edit-profile-close-btn"
            >
              <X size={24} />
            </button>
          </div>

          {/* Body (scrollable) */}
          <div className="overflow-y-auto p-6 flex-1">
            {/* Status Banners */}
            {profile?.status === 'pending' && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-3" data-testid="prof-status-pending">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-yellow-800 text-sm">Profile Under Review</p>
                  <p className="text-xs text-yellow-700">Your profile changes are pending admin approval.</p>
                </div>
              </div>
            )}
            {profile?.status === 'rejected' && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-testid="prof-status-rejected">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800 text-sm">Registration Rejected</p>
                  <p className="text-xs text-red-700">Please edit your details and save to resubmit for approval.</p>
                </div>
              </div>
            )}
            {profile?.adminComments && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3" data-testid="prof-admin-comments">
                <MessageCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800 text-sm">Admin Feedback / प्रशासन की टिप्पणी</p>
                  <p className="text-sm text-amber-700 whitespace-pre-wrap">{profile.adminComments}</p>
                </div>
              </div>
            )}

            {/* Photo Upload */}
            <section className="mb-6">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b pb-2 flex items-center gap-2">
                <Camera className="w-4 h-4 text-primary" /> Profile Photo / प्रोफ़ाइल फ़ोटो
              </h3>
              <div className="flex items-center gap-4">
                <div className="relative w-24 h-24 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center">
                  {currentPhoto ? (
                    <img src={resolveFileUrl(currentPhoto)} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-12 h-12 text-primary" />
                  )}
                </div>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    id="prof-photo-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    data-testid="edit-profile-photo-upload-btn"
                  >
                    {uploadingPhoto ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                    ) : (
                      <><Camera className="w-4 h-4 mr-2" /> {currentPhoto ? 'Change Photo' : 'Upload Photo'}</>
                    )}
                  </Button>
                  <p className="text-xs text-gray-500 mt-1">JPEG, PNG or WebP (max 5MB)</p>
                </div>
              </div>
            </section>

            {/* Personal Details */}
            <section className="mb-6">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b pb-2 flex items-center gap-2">
                <User className="w-4 h-4 text-primary" /> Personal Details / व्यक्तिगत विवरण
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{FIELD_LABELS.fullName}</Label>
                  <Input name="fullName" value={formData.fullName || ''} onChange={handleChange} data-testid="edit-fullName" />
                </div>
                <div>
                  <Label>{FIELD_LABELS.fatherName}</Label>
                  <Input name="fatherName" value={formData.fatherName || ''} onChange={handleChange} data-testid="edit-fatherName" />
                </div>
                <div>
                  <Label>{FIELD_LABELS.motherName}</Label>
                  <Input name="motherName" value={formData.motherName || ''} onChange={handleChange} data-testid="edit-motherName" />
                </div>
                <div>
                  <Label>{FIELD_LABELS.nukh}</Label>
                  <select name="nukh" value={formData.nukh || ''} onChange={handleChange} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                    <option value="">Select Nukh</option>
                    {(options.nukhs || []).map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>{FIELD_LABELS.dateOfBirth}</Label>
                  <Input type="date" name="dateOfBirth" value={formData.dateOfBirth || ''} onChange={handleChange} />
                </div>
              </div>
            </section>

            {/* Contact Details */}
            <section className="mb-6">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b pb-2 flex items-center gap-2">
                <Phone className="w-4 h-4 text-primary" /> Contact Details / संपर्क विवरण
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{FIELD_LABELS.email}</Label>
                  <Input name="email" value={formData.email || ''} disabled className="bg-gray-50" />
                  <p className="text-xs text-gray-400 mt-1">Email cannot be changed</p>
                </div>
                <div>
                  <Label>{FIELD_LABELS.phone}</Label>
                  <Input name="phone" value={formData.phone || ''} onChange={handleChange} data-testid="edit-phone" />
                </div>
                <div>
                  <Label>{FIELD_LABELS.whatsapp}</Label>
                  <Input name="whatsapp" value={formData.whatsapp || ''} onChange={handleChange} />
                </div>
              </div>
            </section>

            {/* Current Location */}
            <section className="mb-6">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b pb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" /> Current Location / वर्तमान स्थान
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{FIELD_LABELS.country}</Label>
                  <Input name="country" value={formData.country || ''} onChange={handleChange} />
                </div>
                <div>
                  <Label>{FIELD_LABELS.state}</Label>
                  <select name="state" value={formData.state || ''} onChange={handleChange} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                    <option value="">Select State</option>
                    {(options.states || []).map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>{FIELD_LABELS.city}</Label>
                  <Input name="city" value={formData.city || ''} onChange={handleChange} />
                </div>
                <div>
                  <Label>{FIELD_LABELS.currentPlace}</Label>
                  <Input name="currentPlace" value={formData.currentPlace || ''} onChange={handleChange} />
                </div>
                <div>
                  <Label>{FIELD_LABELS.pincode}</Label>
                  <Input name="pincode" value={formData.pincode || ''} onChange={handleChange} />
                </div>
                <div className="md:col-span-2">
                  <Label>{FIELD_LABELS.address}</Label>
                  <Input name="address" value={formData.address || ''} onChange={handleChange} />
                </div>
              </div>
            </section>

            {/* Native Place */}
            <section className="mb-6">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b pb-2 flex items-center gap-2">
                <Home className="w-4 h-4 text-primary" /> Native Place / मूल स्थान
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{FIELD_LABELS.nativeVillage}</Label>
                  <Input name="nativeVillage" value={formData.nativeVillage || ''} onChange={handleChange} />
                </div>
                <div>
                  <Label>{FIELD_LABELS.nativeCity}</Label>
                  <Input name="nativeCity" value={formData.nativeCity || ''} onChange={handleChange} />
                </div>
                <div>
                  <Label>{FIELD_LABELS.nativeState}</Label>
                  <Input name="nativeState" value={formData.nativeState || ''} onChange={handleChange} />
                </div>
                <div>
                  <Label>{FIELD_LABELS.nativeCountry}</Label>
                  <Input name="nativeCountry" value={formData.nativeCountry || ''} onChange={handleChange} />
                </div>
              </div>
            </section>

            {/* Professional Details */}
            <section className="mb-6">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b pb-2 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-primary" /> Professional Details / पेशेवर विवरण
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{FIELD_LABELS.profession}</Label>
                  <select name="profession" value={formData.profession || ''} onChange={handleChange} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                    <option value="">Select Profession</option>
                    {(options.professions || []).map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                {formData.profession === 'Other / अन्य' && (
                  <div>
                    <Label>{FIELD_LABELS.professionOther}</Label>
                    <Input name="professionOther" value={formData.professionOther || ''} onChange={handleChange} />
                  </div>
                )}
                <div>
                  <Label>{FIELD_LABELS.specialization}</Label>
                  <Input name="specialization" value={formData.specialization || ''} onChange={handleChange} />
                </div>
                <div>
                  <Label>{FIELD_LABELS.qualification}</Label>
                  <select name="qualification" value={formData.qualification || ''} onChange={handleChange} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                    <option value="">Select Qualification</option>
                    {(options.qualifications || []).map((q) => (
                      <option key={q} value={q}>{q}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>{FIELD_LABELS.experienceYears}</Label>
                  <Input type="number" name="experienceYears" value={formData.experienceYears || ''} onChange={handleChange} />
                </div>
                <div>
                  <Label>{FIELD_LABELS.organization}</Label>
                  <Input name="organization" value={formData.organization || ''} onChange={handleChange} />
                </div>
                <div>
                  <Label>{FIELD_LABELS.designation}</Label>
                  <Input name="designation" value={formData.designation || ''} onChange={handleChange} />
                </div>
                <div className="md:col-span-2">
                  <Label>{FIELD_LABELS.officeAddress}</Label>
                  <Textarea name="officeAddress" value={formData.officeAddress || ''} onChange={handleChange} rows={2} />
                </div>
              </div>
            </section>

            {/* Online Presence */}
            <section className="mb-6">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b pb-2 flex items-center gap-2">
                <Globe className="w-4 h-4 text-primary" /> Online Presence / ऑनलाइन उपस्थिति
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{FIELD_LABELS.website}</Label>
                  <Input name="website" value={formData.website || ''} onChange={handleChange} placeholder="https://" />
                </div>
                <div>
                  <Label>{FIELD_LABELS.linkedIn}</Label>
                  <Input name="linkedIn" value={formData.linkedIn || ''} onChange={handleChange} placeholder="https://linkedin.com/in/..." />
                </div>
              </div>
            </section>

            {/* Bio / About */}
            <section className="mb-6">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b pb-2">About / परिचय</h3>
              <Textarea name="bio" value={formData.bio || ''} onChange={handleChange} rows={4} placeholder="Brief professional bio..." />
            </section>

            {/* Conclave Interest */}
            <section className="mb-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="interestedInConclave2026"
                  checked={!!formData.interestedInConclave2026}
                  onChange={handleChange}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium text-gray-800">Interested in Conclave 2026 / कॉन्क्लेव 2026 में रुचि</span>
              </label>
            </section>

            {/* Additional Info */}
            <section className="mb-6">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b pb-2">Additional Information / Feedback</h3>
              <Textarea
                name="additionalInfo"
                value={formData.additionalInfo || ''}
                onChange={handleChange}
                rows={3}
                placeholder="Share any additional information, feedback or suggestions..."
                data-testid="edit-additional-info"
              />
            </section>
          </div>

          {/* Footer */}
          <div className="border-t bg-gray-50 px-6 py-4 flex justify-end gap-3">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-primary hover:bg-primary/90"
              data-testid="edit-profile-save-btn"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save Changes</>
              )}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ProfessionalEditProfileModal;
